import { useLayoutEffect, useState } from 'react'
import type { RefObject } from 'react'

/** Changes smaller than this, in px, are measurement noise. */
const JITTER = 2

type Size = { width: number; height: number }

/** The element's padding-box size, kept current as it resizes (Apex needs px). */
export const useElementSize = (ref: RefObject<HTMLElement | null>): Size => {
  const [size, setSize] = useState<Size>({ width: 0, height: 0 })

  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    const measure = () => {
      const { clientWidth: width, clientHeight: height } = element
      // Keep the object on a no-op or sub-2px change, so measure-draw-measure cannot loop.
      setSize(current => {
        const same = current.width === width && current.height === height
        const jitter =
          current.width > 0 &&
          current.height > 0 &&
          width > 0 &&
          height > 0 &&
          Math.abs(current.width - width) < JITTER &&
          Math.abs(current.height - height) < JITTER
        return same || jitter ? current : { width, height }
      })
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [ref])

  return size
}
